// Carriles (lanes) para bloques que se solapan dentro de una misma columna.
// Port del sweep-line de la agenda dental (src/utils/appointmentLayout.ts) operando
// sobre minutos-desde-medianoche en vez de timestamps: los horarios de clase son
// semanales recurrentes (weekday + HH:MM), no fechas reales.
// El caller agrupa por columna ANTES de llamar computeLanes (una columna = un salón o un día).

export interface LaneSpan { startMin: number; endMin: number }
export interface LaneInfo { laneIndex: number; totalLanes: number }

const MIN_SPAN_MIN = 5; // defensa contra bloques de duración 0/negativa

interface Internal<T> { item: T; start: number; end: number }

export function computeLanes<T extends LaneSpan>(items: T[]): Map<T, LaneInfo> {
  const out = new Map<T, LaneInfo>();
  if (!items || items.length === 0) return out;

  const internal: Internal<T>[] = items
    .filter((b) => Number.isFinite(b.startMin) && Number.isFinite(b.endMin))
    .map((b) => ({ item: b, start: b.startMin, end: Math.max(b.startMin + MIN_SPAN_MIN, b.endMin) }))
    // inicio ascendente; a igual inicio, el más largo primero (queda en el carril 0, lo que un humano espera)
    .sort((a, b) => (a.start !== b.start ? a.start - b.start : b.end - a.end));

  // Clusters de traslape transitivo: mientras el siguiente inicie ANTES del fin más
  // tardío del cluster, pertenece al mismo. Tocarse exacto (10:00/10:00) NO es traslape.
  const clusters: Internal<T>[][] = [];
  let cluster: Internal<T>[] = [];
  let clusterEnd = -1;
  for (const it of internal) {
    if (cluster.length && it.start < clusterEnd) {
      cluster.push(it);
      clusterEnd = Math.max(clusterEnd, it.end);
    } else {
      if (cluster.length) clusters.push(cluster);
      cluster = [it];
      clusterEnd = it.end;
    }
  }
  if (cluster.length) clusters.push(cluster);

  // Greedy por cluster: primer carril cuyo último bloque ya terminó, o carril nuevo.
  for (const c of clusters) {
    const laneEnds: number[] = [];
    const assigned: { item: T; lane: number }[] = [];
    for (const it of c) {
      let lane = laneEnds.findIndex((end) => end <= it.start);
      if (lane === -1) { lane = laneEnds.length; laneEnds.push(it.end); }
      else laneEnds[lane] = it.end;
      assigned.push({ item: it.item, lane });
    }
    for (const a of assigned) out.set(a.item, { laneIndex: a.lane, totalLanes: laneEnds.length });
  }
  return out;
}

// Posicionamiento horizontal del bloque dentro de su columna según su carril.
// `sub` = franja fija de la columna (sub-columna por salón): los carriles se reparten dentro de ella.
export function laneStyle(laneIndex: number, totalLanes: number, sub?: { index: number; total: number }): React.CSSProperties {
  if (!sub || sub.total <= 1) {
    if (totalLanes <= 1) return { left: 3, right: 3 };
    const pct = 100 / totalLanes;
    return { left: `calc(${laneIndex * pct}% + 2px)`, width: `calc(${pct}% - 4px)`, right: 'auto' };
  }
  const subPct = 100 / sub.total;
  const pct = subPct / Math.max(1, totalLanes);
  return { left: `calc(${sub.index * subPct + laneIndex * pct}% + 2px)`, width: `calc(${pct}% - 4px)`, right: 'auto' };
}
